// Behind the answer (TOM-710, the Marina chat spec): the selected answer's
// evidence beside the chat, on the desktop web only. Everything comes from the
// answer's stored record (lib/neoBehindAnswer.ts): what Neo read, grouped by
// source; the assumptions it used; each calculation in words; what it can't
// tell; what is still unknown. Focus moves into the pane when it opens, and
// back to what opened it when it closes. Styles are in neo-answer.css.

import { useEffect, useId, useLayoutEffect, useRef, type KeyboardEvent } from 'react';
import type { NeoAnswerRecord } from '@neomaris/protocol';

import { neoBehindModel } from '../../lib/neoBehindAnswer';
import { useUnits } from '../../lib/useUnits';
import { Icon } from '../ui/Icon';

export function NeoBehindAnswer({
  record,
  title,
  focusEvidence,
  focusNonce = 0,
  keepFocus = false,
  onClose,
}: {
  record: NeoAnswerRecord;
  /** The answer's conclusion, or its question when it has none. */
  title: string;
  /** An evidence item to bring into view and mark: a "Show the evidence" chip's. */
  focusEvidence?: string;
  /** Bumped by each open, so the same chip clicked again brings its item back. */
  focusNonce?: number;
  /**
   * Opened by a new answer arriving, not by the owner (TOM-724): the focus
   * stays where it is, so a reply never pulls it out of the composer.
   */
  keepFocus?: boolean;
  onClose: () => void;
}) {
  const units = useUnits();
  const headingId = useId();
  const headingRef = useRef<HTMLHeadingElement | null>(null);
  const asideRef = useRef<HTMLElement | null>(null);
  const model = neoBehindModel(record, units, Date.now());

  // Opened, focus goes in; closed, it goes back to whatever opened the pane,
  // but only from inside the pane: a board taking the slot, or a narrower
  // window, never pulls the focus away from where the owner is typing.
  // One pane per answer (keyed), so how it opened is fixed at mount.
  const keepFocusOnOpen = useRef(keepFocus);
  useLayoutEffect(() => {
    const aside = asideRef.current;
    const opener = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    // Kept only when it is somewhere: focus left on the page itself is lost.
    const nowhere = opener === null || opener === document.body;
    if (!keepFocusOnOpen.current || nowhere) headingRef.current?.focus({ preventScroll: true });
    return () => {
      const inside = aside !== null && aside.contains(document.activeElement);
      if (inside && opener?.isConnected === true) opener.focus({ preventScroll: true });
    };
  }, []);

  // Another answer or another item: the pane moves to it.
  useEffect(() => {
    if (focusEvidence === undefined) {
      if (!keepFocus) headingRef.current?.focus({ preventScroll: true });
      return;
    }
    const item = document.getElementById(`neo-behind-${focusEvidence}`);
    item?.scrollIntoView({ block: 'nearest', behavior: 'instant' });
    item?.focus({ preventScroll: true });
  }, [record, focusEvidence, focusNonce, keepFocus]);

  function onKeyDown(event: KeyboardEvent<HTMLElement>) {
    if (event.key !== 'Escape') return;
    event.stopPropagation();
    onClose();
  }

  return (
    <aside
      ref={asideRef}
      className="neo-behind"
      aria-labelledby={headingId}
      data-testid="neo-behind"
      onKeyDown={onKeyDown}
    >
      <header className="neo-behind__head">
        <div className="neo-behind__titles">
          <p className="neo-behind__eyebrow">Behind the answer</p>
          <h2 id={headingId} ref={headingRef} tabIndex={-1} className="neo-behind__title">
            {title}
          </h2>
        </div>
        <button
          type="button"
          className="tb-icon-btn neo-behind__close"
          aria-label="Close Behind the answer"
          title="Close"
          data-testid="neo-behind-close"
          onClick={onClose}
        >
          <Icon name="x" size={18} />
        </button>
      </header>

      {model.sources.map((source) => (
        <section key={source.provider} className="neo-behind__card" aria-label={source.provider}>
          <h3 className="neo-behind__card-title">{source.provider}</h3>
          <ul className="neo-behind__list">
            {source.items.map((item) => (
              <li
                key={item.id}
                id={`neo-behind-${item.id}`}
                tabIndex={-1}
                className={`neo-behind__item${item.id === focusEvidence ? ' is-focused' : ''}`}
                data-testid="neo-behind-source"
              >
                <div className="neo-behind__row">
                  <span className="neo-behind__label">{item.label}</span>
                  {item.value !== null ? (
                    <span className="neo-behind__value">{item.value}</span>
                  ) : null}
                </div>
                <p className="neo-behind__meta">
                  {[item.range, item.when, item.coverage, item.method]
                    .filter((part): part is string => part !== null)
                    .join(' · ')}
                  <span className={`neo-behind__fresh is-${item.freshness.tone}`}>
                    {item.freshness.text}
                  </span>
                </p>
                {item.limitations.map((text) => (
                  <p key={text} className="neo-behind__limit">
                    {text}
                  </p>
                ))}
              </li>
            ))}
          </ul>
        </section>
      ))}

      {model.assumptions.length > 0 ? (
        <section className="neo-behind__card" aria-label="Assumptions">
          <h3 className="neo-behind__card-title">Assumptions</h3>
          <ul className="neo-behind__list">
            {model.assumptions.map((a) => (
              <li key={a.id} className="neo-behind__item" data-testid="neo-behind-assumption">
                <div className="neo-behind__row">
                  <span className="neo-behind__label">{a.label}</span>
                  <span className="neo-behind__value">{a.value}</span>
                </div>
                <p className="neo-behind__meta">
                  <span className={`neo-behind__origin is-${a.origin}`}>{a.originText}</span>
                </p>
              </li>
            ))}
          </ul>
        </section>
      ) : null}

      {model.results.length > 0 ? (
        <section className="neo-behind__card" aria-label="How it was worked out">
          <h3 className="neo-behind__card-title">How it was worked out</h3>
          <ul className="neo-behind__list">
            {model.results.map((r) => (
              <li key={r.id} className="neo-behind__item" data-testid="neo-behind-result">
                <div className="neo-behind__row">
                  <span className="neo-behind__label">{r.label}</span>
                  <span className="neo-behind__value">{r.value}</span>
                </div>
                {r.words !== null ? <p className="neo-behind__meta">{r.words}</p> : null}
              </li>
            ))}
          </ul>
        </section>
      ) : null}

      {model.limitations.length > 0 ? (
        <section className="neo-behind__card" aria-label="What this can’t tell you">
          <h3 className="neo-behind__card-title">What this can’t tell you</h3>
          <ul className="neo-behind__notes">
            {model.limitations.map((text) => (
              <li key={text}>{text}</li>
            ))}
          </ul>
        </section>
      ) : null}

      {model.missing.length > 0 ? (
        <section className="neo-behind__card" aria-label="Still unknown">
          <h3 className="neo-behind__card-title">Still unknown</h3>
          <ul className="neo-behind__notes">
            {model.missing.map((text) => (
              <li key={text}>{text}</li>
            ))}
          </ul>
        </section>
      ) : null}

      <p className="neo-behind__foot">As Neo read it when it answered.</p>
    </aside>
  );
}
